import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  inject,
  Injectable
} from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { SwUpdate } from "@angular/service-worker";
import Swal from "sweetalert2";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import semverDiff from "semver-diff";
import { ProductService } from "src/app/services/product.service";
import {
  getCurrentSoftwareProduct,
  getCurrentSoftwareProductVersion,
  SoftwareProduct
} from "src/app/support/product";
import { MemberStorageService } from "src/app/services/member-storage.service";
import { ChangelogHistoryService } from "src/app/services/changelog-history.service";
import {
  TaskExecutionPlan,
  TasksService,
  TaskType
} from "src/app/services/tasks.service";
// @ts-ignore
import { el } from "src/app/support/dom";
import { environment } from "src/environments/environment";
import { PLATFORM_TOKEN } from "src/app/tokens/platform.token";
import { PlatformType } from "src/app/common/platform-type";
import { Mutex } from "async-mutex";
import type { ChangelogComponent } from "src/app/components/ui/changelog/changelog.component";

export enum UpdateServiceStatus {
  DISABLED = "disabled",
  STANDBY = "standby",
  CHECK = "check",
  UPDATING = "updating",
  UPDATE_READY = "update-ready",
  UPDATE_ERROR = "update-error"
}

@Injectable()
export class UpdateService {
  private readonly applicationRef = inject(ApplicationRef);
  private readonly swUpdate = inject(SwUpdate);
  private readonly memberStorageService = inject(MemberStorageService);
  private readonly tasksService = inject(TasksService);
  private readonly changelogHistoryService = inject(ChangelogHistoryService);
  private readonly PLATFORM = inject(PLATFORM_TOKEN);

  private mutex = new Mutex();

  productService = inject(ProductService);

  updating$ = new BehaviorSubject<UpdateServiceStatus>(
    UpdateServiceStatus.DISABLED
  );

  constructor() {
    this.tasksService.schedule({
      plan: TaskExecutionPlan.WHEN_STABLE,
      type: TaskType.RUN_ONCE,
      fn: () => {
        this.updating$.subscribe(async (updateStatus: UpdateServiceStatus) => {
          console.log(`[UpdateService] status "${updateStatus}"`);

          switch (updateStatus) {
            case UpdateServiceStatus.CHECK: {
              console.log(`[UpdateService] checkForUpdateResult called`);

              await this.mutex.runExclusive(async () => {
                await this.swUpdate.checkForUpdate().then((result) => {
                  console.log(
                    `[UpdateService] checkForUpdateResult result ${result}`
                  );

                  if (result) {
                    this.updating$.next(UpdateServiceStatus.UPDATE_READY);
                  }
                });
              });

              break;
            }
            case UpdateServiceStatus.UPDATE_READY: {
              await this.updateReady();
              break;
            }
          }
        });
      }
    });
  }

  async updateReady(): Promise<void> {
    const result = await Swal.fire({
      toast: true,
      position:
        this.PLATFORM === PlatformType.DESKTOP ? "bottom-right" : "bottom",
      title: `🚀 ${sntTranslate(marker("Nuovo aggiornamento"))}!`,
      text:
        this.productService.getCurrentProduct() === SoftwareProduct.PMS
          ? sntTranslate(
              marker(
                "Snoots PMS è stato aggiornato! Per non perderti le ultime novità e miglioramenti ricarica ora il PMS!"
              )
            )
          : sntTranslate(
              marker(
                "Rocket è stato aggiornato! Per non perderti le ultime novità e miglioramenti ricarica ora!"
              )
            ),
      width: 460,
      confirmButtonText: sntTranslate(marker("Ricarica ora")),
      cancelButtonText: sntTranslate(marker("In seguito")),
      showCancelButton: true,
      customClass: {
        container:
          this.PLATFORM === PlatformType.DESKTOP
            ? "swal-platform-desktop"
            : "swal-platform-mobile"
      }
    });

    if (result.isConfirmed) {
      window.location.reload();
    }
  }

  async enableAutomaticUpdates(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      console.log(`[UpdateService] DISABLED`);
      return;
    }

    this.updating$.next(UpdateServiceStatus.STANDBY);

    this.swUpdate.versionUpdates.subscribe(async (updateEvent) => {
      console.groupCollapsed(`[UpdateService] ${updateEvent.type}`);
      console.dir(updateEvent);
      console.groupEnd();

      switch (updateEvent.type) {
        case "VERSION_DETECTED": {
          this.updating$.next(UpdateServiceStatus.UPDATING);
          break;
        }
        case "NO_NEW_VERSION_DETECTED": {
          this.updating$.next(UpdateServiceStatus.STANDBY);
          break;
        }
        case "VERSION_INSTALLATION_FAILED": {
          this.updating$.next(UpdateServiceStatus.UPDATE_ERROR);
          break;
        }
        case "VERSION_READY": {
          this.updating$.next(UpdateServiceStatus.UPDATE_READY);
          break;
        }
      }
    });

    this.tasksService.schedule({
      plan: TaskExecutionPlan.WHEN_STABLE,
      type: TaskType.RECURRING,
      interval: environment.snoots.service_worker.check_update_interval,
      fn: () => {
        this.updating$.next(UpdateServiceStatus.CHECK);
      }
    });
  }

  check(): void {
    this.updating$.next(UpdateServiceStatus.CHECK);
  }

  async checkIfLoadedNewVersion(): Promise<void> {
    const currentVersion: string = getCurrentSoftwareProductVersion();

    const lastVersionInstalled = this.memberStorageService.getItem(
      "last-loaded-version"
    );

    if (
      !lastVersionInstalled ||
      semverDiff(lastVersionInstalled || "", currentVersion)
    ) {
      await this.showNewVersionInfo(currentVersion);
      return;
    }
  }

  private async showNewVersionInfo(version: string): Promise<void> {
    let componentRef: ComponentRef<ChangelogComponent> | null = null;
    let dismissSub: { unsubscribe(): void } | undefined;

    try {
      const componentType =
        await import("../components/ui/changelog/changelog.component").then(
          (m) => m.ChangelogComponent
        );

      const hostElement = el("div");

      componentRef = createComponent(componentType, {
        environmentInjector: this.applicationRef.injector,
        hostElement
      });

      const changelogEntry =
        this.changelogHistoryService.getLastChangelogByProduct(
          getCurrentSoftwareProduct()
        );

      if (changelogEntry?.advanced) {
        componentRef.setInput("template", "advanced");
      }

      dismissSub = componentRef.instance.dismiss.subscribe(() => {
        Swal.close();
      });

      this.applicationRef.attachView(componentRef.hostView);
      componentRef.changeDetectorRef.detectChanges();

      await Swal.fire({
        title: undefined,
        html: hostElement,
        position: "bottom-right",
        toast: true,
        showCancelButton: false,
        showConfirmButton: false,
        // Toast + no buttons => Swal closes on any popup click; close UI is in the component.
        showCloseButton: true,
        width: 520,
        heightAuto: true,
        customClass: {
          container: ["m-0"],
          htmlContainer: ["m-0"],
          popup: ["p-0"],
          closeButton: ["swal-changelog-hide-native-close"]
        }
      });

      this.memberStorageService.setItem("last-loaded-version", version);
    } catch (ex) {
      console.error(ex);
    } finally {
      dismissSub?.unsubscribe();

      if (componentRef) {
        this.applicationRef.detachView(componentRef.hostView);
        componentRef.destroy();
      }
    }
  }
}
